import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
} from '@mui/material';
import { mergeAccount } from '../../api/accounts';
import styles from './AccountMergeDialog.module.css';

// Super-admin only: merge a fragment/ghost account into a survivor. The merging
// side's balances move to the survivor; the merged node is tombstoned. Mirrors
// the backend contract in .claude/references/account-identity-correction.md.

const accountLabel = (a) => a?._displayName
  || `${a?.bank_name || 'Account'} *${a?.account_number_last4 || '????'}`;

export default function AccountMergeDialog({ open, account, candidates, onClose, onSaved }) {
  const options = useMemo(
    () => (candidates || []).filter((c) => c && c.id && c.id !== account?.id),
    [candidates, account],
  );

  const [survivorId, setSurvivorId] = useState('');
  const [reason, setReason] = useState('');
  // Default to the historical (invisible) behavior; only a deliberate reissue
  // merge switches to keeping the old number visible.
  const [mergeKind, setMergeKind] = useState('DUPLICATE_OR_ERROR');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSurvivorId('');
      setReason('');
      setMergeKind('DUPLICATE_OR_ERROR');
      setError(null);
    }
  }, [open]);

  const canSubmit = Boolean(survivorId) && reason.trim().length > 0 && !saving;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await mergeAccount(account.id, survivorId, reason.trim(), mergeKind);
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e?.message || 'The accounts could not be merged.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge account</DialogTitle>
      <DialogContent>
        <div className={styles.warning} role="alert">
          <div className={styles.warningTitle}>
            This merges <strong>{accountLabel(account)}</strong> into another account.
          </div>
          <p className={styles.warningBody}>
            Its statements move to the survivor and it is tombstoned — it disappears
            from reports. Duplicate-period statements are marked merged, not moved.
            This is audited and cannot be undone here.
          </p>
        </div>

        <div className={styles.field}>
          <TextField
            select label="Survivor account" value={survivorId} fullWidth size="small"
            required helperText="The account that keeps its identity and receives the statements."
            onChange={(e) => setSurvivorId(e.target.value)}
          >
            {options.length === 0 && <MenuItem disabled value="">No other accounts</MenuItem>}
            {options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>{accountLabel(opt)}</MenuItem>
            ))}
          </TextField>
        </div>

        <div className={styles.field}>
          <TextField
            select label="What kind of merge is this?" value={mergeKind} fullWidth size="small"
            helperText={mergeKind === 'ACCOUNT_NUMBER_CHANGED'
              ? "The old account number stays visible under the survivor, so you can track reissued cards."
              : "For duplicates or scan errors — the merged account disappears from reports."}
            onChange={(e) => setMergeKind(e.target.value)}
          >
            <MenuItem value="DUPLICATE_OR_ERROR">Duplicate / scan error (don’t surface)</MenuItem>
            <MenuItem value="ACCOUNT_NUMBER_CHANGED">Account number changed (keep it visible)</MenuItem>
          </TextField>
        </div>

        <div className={styles.field}>
          <TextField
            label="Reason" value={reason} fullWidth required multiline minRows={2} size="small"
            helperText="Recorded on the audit trail for this merge."
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {error && <div className={styles.error} role="alert">{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {saving ? 'Merging…' : 'Merge accounts'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
